/**
 * Analytics utility for tracking upload flow events
 * Integrates with PostHog or custom analytics
 */

// Track if event has already been fired to prevent duplicates
const firedEvents = new Set();

/**
 * Fire analytics event (only once per event type per session)
 * @param {string} eventName - Event name
 * @param {Object} properties - Event properties
 */
export const trackEvent = (eventName, properties = {}) => {
  // Create unique key for this event
  const eventKey = `${eventName}_${JSON.stringify(properties)}`;
  
  // Check if already fired
  if (firedEvents.has(eventKey)) {
    console.log(`[Analytics] Event already fired: ${eventName}`);
    return;
  }

  // Mark as fired
  firedEvents.add(eventKey);

  // Log to console in development
  console.log(`[Analytics] ${eventName}`, properties);

  // PostHog integration (if available)
  if (window.posthog) {
    try {
      window.posthog.capture(eventName, properties);
    } catch (error) {
      console.error('[Analytics] PostHog error:', error);
    }
  }

  // Add other analytics integrations here (Google Analytics, Mixpanel, etc.)
};

/**
 * Track upload started
 * @param {Object} fileInfo - File information
 */
export const trackUploadStarted = (fileInfo = {}) => {
  trackEvent('upload_started', {
    file_type: fileInfo.type,
    file_size: fileInfo.size,
    timestamp: new Date().toISOString(),
  });
};

/**
 * Track upload success
 * @param {Object} data - Upload response data
 */
export const trackUploadSuccess = (data = {}) => {
  trackEvent('upload_success', {
    job_id: data.jobId,
    timestamp: new Date().toISOString(),
  });
};

/**
 * Track upload failure
 * @param {Object} error - Error information
 */
export const trackUploadFailure = (error = {}) => {
  trackEvent('upload_failure', {
    error_type: error.errorType,
    error_message: error.message,
    timestamp: new Date().toISOString(),
  });
};

/**
 * Track polling started
 * @param {string} jobId - Job ID
 */
export const trackPollingStarted = (jobId) => {
  trackEvent('polling_started', {
    job_id: jobId,
    timestamp: new Date().toISOString(),
  });
};

/**
 * Track polling finished
 * @param {Object} data - Polling result data
 */
export const trackPollingFinished = (data = {}) => {
  trackEvent('polling_finished', {
    job_id: data.jobId,
    status: data.status,
    duration: data.duration,
    timestamp: new Date().toISOString(),
  });
};

/**
 * Track error encountered
 * @param {Object} error - Error information
 */
export const trackErrorEncountered = (error = {}) => {
  trackEvent('error_encountered', {
    error_type: error.errorType,
    error_message: error.message,
    context: error.context,
    timestamp: new Date().toISOString(),
  });
};

/**
 * Track layout opened
 * @param {string} galleryUrl - Gallery URL
 */
export const trackLayoutOpened = (galleryUrl) => {
  trackEvent('layout_opened', {
    gallery_url: galleryUrl,
    timestamp: new Date().toISOString(),
  });
};

/**
 * Reset fired events (useful for "Try Again" functionality)
 */
export const resetAnalytics = () => {
  firedEvents.clear();
  console.log('[Analytics] Events reset');
};

const analytics = {
  trackEvent,
  trackUploadStarted,
  trackUploadSuccess,
  trackUploadFailure,
  trackPollingStarted,
  trackPollingFinished,
  trackErrorEncountered,
  trackLayoutOpened,
  resetAnalytics,
};
export default analytics;
